import React, { useState } from 'react';
import { Button } from '../Button/Button';
import { Modal } from '../Modal/Modal';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsPageHeader,
  SettingsRow,
} from '../SettingsShell/SettingsPage';
import { SetSwitch } from '../SettingsShell/controls';
import { tcCampaignStore } from '../../treatment/mockData';

/** The treatments ELVA writes in patient words (spec 48 §1 / §2 picker). */
const ELVA_TREATMENTS = ['Crown', 'Filling', 'Root canal', 'Deep cleaning', 'Implant', 'Removing a tooth', 'Bridge', 'Denture', 'Veneer', 'Night guard'];

interface PlanSample { name: string; why: string; ifWait: string }

export interface TcSettingsViewProps {
  onOpenCampaigns?: () => void;
  onPreviewPlan?: () => void;
}

/**
 * Settings › Treatment Coordinator — rebuilt (amendment 27 §8), then trimmed to
 * **no locked rows** (spec 40).
 *
 * The rule (Amir, 2 Oct): a settings page shows only what the practice can change.
 * A row that's always on and can't be changed isn't a setting, so it isn't shown.
 * That dropped the whole first card of fixed conditions, the four locked rows under
 * the plan table, and the one locked toggle plus two fixed rows on the Calls card —
 * no lock icons, no Locked or Always pills remain. The agent still behaves exactly
 * the same; those rules just aren't listed here.
 *
 * What's left: the plan page (Preview + the editable Why / If-you-wait table) and
 * the card for what puts a patient in Calls (three switches + one plain reply line).
 * Who gets messaged and what they read is set per campaign, not here.
 */
export function TcSettingsView({ onOpenCampaigns, onPreviewPlan }: TcSettingsViewProps) {
  const active = tcCampaignStore.campaigns.filter((c) => c.state === 'active').length;
  const drafts = tcCampaignStore.campaigns.filter((c) => c.state === 'draft').length;
  const [showAllPlan, setShowAllPlan] = useState(false);
  const [samples, setSamples] = useState<PlanSample[]>(PLAN_SAMPLES);
  const [editor, setEditor] = useState<{ index: number | null; name: string; why: string; ifWait: string } | null>(null);

  const visible = showAllPlan ? samples : samples.slice(0, 5);
  const moreCount = samples.length - 5;
  const moreNames = samples.slice(5).map((s) => s.name).join(', ');

  const openAdd = () => { setEditor({ index: null, name: '', why: '', ifWait: '' }); };
  const openEdit = (i: number) => { const s = samples[i]; if (s) setEditor({ index: i, name: s.name, why: s.why, ifWait: s.ifWait }); };
  const remove = (i: number) => {
    if (!window.confirm('Remove this sample? ELVA will use its own standard text for this treatment.')) return;
    setSamples(samples.filter((_, j) => j !== i));
  };
  const save = () => {
    if (editor === null) return;
    const next: PlanSample = { name: editor.name, why: editor.why, ifWait: editor.ifWait };
    setSamples(editor.index === null ? [...samples, next] : samples.map((s, j) => (j === editor.index ? next : s)));
    setEditor(null);
  };
  // One sample per treatment: in Add mode, offer only treatments not already used.
  const takenNames = new Set(samples.map((s) => s.name));
  // If you wait can be empty (a veneer, whitening) — only the treatment + Why are required.
  const canSave = editor !== null && editor.name !== '' && editor.why.trim() !== '';

  return (
    <div className="min-w-0 flex-1">
      <SettingsPageHeader
        eyebrow="Settings · AI Brain"
        titleLead="Treatment"
        titleAccent="Coordinator"
        sub="Texts and emails about treatment that’s diagnosed but not booked, a private plan page for each patient, and a Call list for your team. ELVA doesn’t make calls for this."
      />

      {/* banner — the settings that matter live in each campaign */}
      <div className="mb-[26px] flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-[var(--border-default)] bg-[var(--brand-indigo-tint)] px-4 py-3">
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold text-[var(--text-primary)]">Who gets messaged, and what they read, is set in each campaign</span>
          <span className="block text-[11.5px] text-[var(--text-muted)]">Treatment Intelligence › Campaigns · {active} active, {drafts} draft</span>
        </span>
        <Button variant="primary" size="sm" onClick={onOpenCampaigns}>Open campaigns</Button>
      </div>

      <SettingsCards>
        {/* 01 — how ELVA explains treatment */}
        <SettingsCard
          n="01"
          title="How ELVA explains treatment"
          sub="ELVA writes a short Why and If you wait for each patient, from what the dentist recorded in their chart. These samples show ELVA how your practice says things. It copies their tone and length."
          headRight={<Button variant="secondary" size="sm" onClick={onPreviewPlan}>Preview a plan page</Button>}
        >
          <div className="px-6 py-4">
            <div className="overflow-hidden rounded-[10px] border border-[var(--border-default)]">
              <div className="grid grid-cols-[0.8fr_1.3fr_1.3fr_0.5fr] gap-x-3 border-b border-[var(--border-default)] bg-[#F4F6FB] px-3 py-2 text-[10px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                <span>Treatment</span><span>Why</span><span>If you wait</span><span />
              </div>
              {visible.map((t) => {
                const idx = samples.indexOf(t);
                return <PlanSampleRow key={t.name} row={t} last={idx === visible.length - 1} onEdit={() => { openEdit(idx); }} onRemove={() => { remove(idx); }} />;
              })}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
              {moreCount > 0 ? (
                <button type="button" onClick={() => { setShowAllPlan((v) => !v); }} className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
                  {showAllPlan ? 'Show fewer' : `+ ${String(moreCount)} more · ${moreNames}`}
                </button>
              ) : null}
              <button type="button" onClick={openAdd} className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">+ Add a sample</button>
            </div>
            <div className="mt-3 rounded-[8px] bg-[var(--surface-sunken)] px-3 py-2 text-[11.5px] leading-[1.5] text-[var(--text-muted)]">
              When a chart doesn’t say why, ELVA only explains what the treatment does. To change what one patient reads, open Patients › Treatments.
            </div>
          </div>
        </SettingsCard>

        {/* 02 — what puts a patient in Calls */}
        <SettingsCard n="02" title="What puts a patient in Calls" sub="What puts a patient on your team’s Calls page, in Treatment Intelligence.">
          <CallListToggle label="Viewed their x-ray" defaultOn />
          <CallListToggle label="Opened their plan 2 or more times" desc="Opens count only after the date of birth." defaultOn />
          <CallListToggle label="Started booking and didn’t finish" defaultOn last />
          <div className="px-6 pb-[18px] pt-1 text-[12.5px] text-[var(--text-muted)]">
            A patient who replies about their plan always goes to Calls.
          </div>
        </SettingsCard>
      </SettingsCards>

      <SettingsFooter location="Settings · AI Brain · Treatment Coordinator" />

      {editor !== null ? (
        <Modal
          title={editor.index === null ? 'Add a sample' : `Edit the ${editor.name} sample`}
          onClose={() => { setEditor(null); }}
          className="w-[min(560px,calc(100vw-32px))]"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => { setEditor(null); }}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={!canSave} onClick={save}>{editor.index === null ? 'Add' : 'Save'}</Button>
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            {editor.index === null ? (
              <label className="block">
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--text-muted)]">Treatment</span>
                <select
                  value={editor.name}
                  onChange={(e) => { setEditor({ ...editor, name: e.target.value }); }}
                  className="w-full rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-2.5 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
                >
                  <option value="">Pick a treatment…</option>
                  {ELVA_TREATMENTS.filter((t) => !takenNames.has(t)).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
            ) : (
              <div className="text-[13px] font-semibold text-[var(--text-primary)]">{editor.name}</div>
            )}
            <SampleField label="Why" value={editor.why} onChange={(v) => { setEditor({ ...editor, why: v }); }} />
            <SampleField label="If you wait" value={editor.ifWait} onChange={(v) => { setEditor({ ...editor, ifWait: v }); }} />
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

/** A 220-character text area with a counter (spec 48 §2). */
function SampleField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const MAX = 220;
  return (
    <label className="block">
      <span className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--text-muted)]">{label}</span>
        <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--text-muted)]">{value.length}/{MAX}</span>
      </span>
      <textarea
        value={value}
        rows={3}
        maxLength={MAX}
        onChange={(e) => { onChange(e.target.value); }}
        className="w-full rounded-[9px] border border-[var(--border-default)] px-2.5 py-2 text-[12.5px] leading-[1.5] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
      />
    </label>
  );
}

// The samples — one per treatment, in ELVA's patient words. The first 5 show; the
// rest are behind "+ N more". A sample is also the fallback when a chart says no why.
const PLAN_SAMPLES: PlanSample[] = [
  { name: 'Crown', why: 'The tooth is weak or cracked. A crown covers it and holds it together.', ifWait: 'A crack can grow until the tooth breaks. That can mean a root canal or losing it.' },
  { name: 'Root canal', why: 'The nerve inside the tooth is infected or dying.', ifWait: 'The infection can spread and the tooth may need to come out.' },
  { name: 'Implant', why: 'Replaces a missing tooth with a fixed one.', ifWait: 'Nearby teeth can drift into the gap and the bone slowly shrinks.' },
  { name: 'Deep cleaning', why: 'Early gum pockets were found. This clears them out.', ifWait: 'Gums can pull back and teeth can loosen over time.' },
  { name: 'Filling', why: 'There’s a small cavity.', ifWait: 'It grows, and a bigger one can need a crown or a root canal.' },
  { name: 'Bridge', why: 'Fills a gap using the teeth on either side.', ifWait: 'The gap widens and nearby teeth tilt in.' },
  { name: 'Removing a tooth', why: 'The tooth can’t be saved and needs to come out.', ifWait: 'Pain and infection get worse and can spread.' },
  { name: 'Denture', why: 'Replaces several missing teeth you can take out.', ifWait: 'Eating gets harder and the jaw bone shrinks.' },
  { name: 'Veneer', why: 'A thin cover for a chipped or stained front tooth.', ifWait: 'The chip can catch and grow.' },
  { name: 'Night guard', why: 'Protects your teeth from grinding at night.', ifWait: 'Grinding wears the teeth down and can crack them.' },
];

function PlanSampleRow({ row, last, onEdit, onRemove }: { row: PlanSample; last: boolean; onEdit: () => void; onRemove: () => void }) {
  return (
    <div className="grid grid-cols-[0.8fr_1.3fr_1.3fr_0.5fr] items-start gap-x-3 px-3 py-2.5 text-[12px]" style={last ? undefined : { borderBottom: '1px solid #F0F1F4' }}>
      <span className="font-semibold text-[var(--text-primary)]">{row.name}</span>
      <span className="leading-[1.5] text-[var(--text-secondary)]">{row.why}</span>
      <span className="leading-[1.5] text-[var(--text-secondary)]">{row.ifWait}</span>
      <span className="flex justify-end gap-3">
        <button type="button" onClick={onEdit} className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">Edit</button>
        <button type="button" onClick={onRemove} className="text-[11.5px] font-semibold text-[var(--text-muted)] hover:text-[var(--alert-red)] hover:underline">Remove</button>
      </span>
    </div>
  );
}

function CallListToggle({ label, desc, defaultOn, last }: { label: string; desc?: string; defaultOn?: boolean; last?: boolean }) {
  const [on, setOn] = useState(defaultOn ?? false);
  return (
    <SettingsRow
      label={label}
      desc={desc}
      last={last === true}
      control={<SetSwitch label={label} checked={on} onChange={setOn} />}
    />
  );
}
